// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useInsertSmsMessageMutation } from '../mutations/insertSmsMessage.mutation.generated';
import { toast } from 'react-toastify';
import { markMessagesAsRead } from '@features/moves/services/smsReadStatusTracker';
import { useUser } from '@store';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

// TYPES ---------------------------------------------------------------- //

interface UseSendMessageOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseSendMessage {
  sendMessage: (messageBody: string) => Promise<void>;
  sending: boolean;
  error?: Error;
}

// HOOK ---------------------------------------------------------------- //

export const useSendMessage = (
  moveId: number | undefined,
  recipientPhone: string | undefined,
  senderPhone: string | undefined,
  options: UseSendMessageOptions = {}
): UseSendMessage => {
  const [insertSmsMessage, { loading }] = useInsertSmsMessageMutation({
    errorPolicy: 'all',
  });
  const user = useUser();
  const isSmsEnabled = useValetSelfManaged();

  const sendMessage = React.useCallback(async (messageBody: string) => {
    console.log('Sending message with:', { moveId, recipientPhone, senderPhone, messageBody: messageBody.trim() });

    // SMS is a self-managed feature.
    if (!isSmsEnabled) {
      toast.error('SMS messaging is only available for self-managed customers.', { toastId: 'move-chat-send' });
      return;
    }
    
    if (!moveId) {
      toast.error('Unable to send message. Move ID is missing.', { toastId: 'move-chat-send' });
      return;
    }
    
    if (!recipientPhone) {
      toast.error('Unable to send message. Customer phone number is missing.', { toastId: 'move-chat-send' });
      return;
    }
    
    if (!messageBody.trim()) {
      toast.error('Please enter a message.', { toastId: 'move-chat-send' });
      return;
    }

    // Use provided sender phone or fallback to 'dealer'
    const finalSenderPhone = senderPhone || 'dealer';

    try {
      const result = await insertSmsMessage({
        variables: {
          move_id: moveId,
          recipient_phone: recipientPhone,
          sender_phone: finalSenderPhone,
          message_body: messageBody.trim(),
          direction: 'outbound',
          status: 'initiating',
          createdby: user?.profile?.display_name || 'Unknown User',
        },
      });

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to send message');
      }

      // Mark messages as read after successful send (since dealer is responding)
      // Get the message ID from the result to mark as read
      const newMessageId = result.data?.insert_smsmessages_one?.id;
      if (newMessageId) {
        markMessagesAsRead(moveId, String(newMessageId));
      }

      if (options.onSuccess) {
        options.onSuccess();
      }
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Failed to send message. Please try again.', { toastId: 'move-chat-send' });
      
      if (options.onError) {
        options.onError(error as Error);
      }
    }
  }, [moveId, recipientPhone, senderPhone, insertSmsMessage, options, user, isSmsEnabled]);

  return {
    sendMessage,
    sending: loading,
    error: undefined,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default useSendMessage;